import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { BlueskyIcon } from '../components/BlueskyIcon';
import { ConfirmModal } from '../components/ConfirmModal';
import { PlanUsageDisplay } from '../components/PlanUsageDisplay';
import { ReservedActionListItem } from '../components/ReservedActionListItem';
import { UserStatusReference } from '../components/UserStatusReference';
import {
  apiClient,
  type PlanUsageInfo,
  type ReservedAction,
  type SchedulerAuthorization,
  UserStatus
} from '../lib/api-client';
import { calculateTimeRemaining, formatDateTime } from '../utils/datetime';

// 予約アクション一覧の1ページあたりの表示件数（既存の Recent Posts 実装の limit:10 を踏襲）
const RESERVED_ACTIONS_PAGE_SIZE = 10;

interface User {
  id: string;
  did: string;
  handle: string;
  display_name: string | null;
  avatar: string | null;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  has_access_keyword_access: boolean;
  status: UserStatus;
  upgrade_prohibited: boolean;
  suspended_until?: string | null;
  suspension_reason?: string | null;
}

interface PlanAssignment {
  id: string;
  planId: string;
  plan: {
    id: string;
    tier: string;
    name: string;
    displayName: Record<string, string>;
    limits: Record<string, number | boolean | null>;
  };
  ticketId: string | null;
  ticket: {
    id: string;
    code: string;
  } | null;
  activatedAt: string;
  expiresAt: string | null;
  status: string;
  createdAt: string;
}

export default function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [planAssignments, setPlanAssignments] = useState<PlanAssignment[]>([]);
  const [upgradeProhibited, setUpgradeProhibited] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Suspension form state
  const [suspensionType, setSuspensionType] = useState<'temporary' | 'permanent'>('temporary');
  const [suspensionDays, setSuspensionDays] = useState<string>('7');
  const [suspensionUntil, setSuspensionUntil] = useState<string>('');
  const [suspensionStatus, setSuspensionStatus] = useState<'SUSPENDED' | 'BLOCKED'>('SUSPENDED');
  const [suspensionReason, setSuspensionReason] = useState<string>('');

  // UI collapse state
  const [isUserManagementExpanded, setIsUserManagementExpanded] = useState(false);
  const [isSuspensionExpanded, setIsSuspensionExpanded] = useState(false);
  const [isSchedulerExpanded, setIsSchedulerExpanded] = useState(false);

  // Usage stats state
  const [userUsage, setUserUsage] = useState<PlanUsageInfo | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  // Scheduler authorization state
  const [schedulerAuthorization, setSchedulerAuthorization] =
    useState<SchedulerAuthorization | null>(null);
  const [loadingScheduler, setLoadingScheduler] = useState(false);

  // Reserved actions (予約投稿・予約リポスト・予約削除の統合一覧) state
  const [reservedActions, setReservedActions] = useState<ReservedAction[]>([]);
  const [reservedActionsPage, setReservedActionsPage] = useState(1);
  const [reservedActionsTotalPages, setReservedActionsTotalPages] = useState(0);
  const [reservedActionsTotal, setReservedActionsTotal] = useState(0);
  const [reservedActionStatusFilter, setReservedActionStatusFilter] = useState('');
  const [reservedActionKindFilter, setReservedActionKindFilter] = useState('');
  const [loadingReservedActions, setLoadingReservedActions] = useState(false);
  // 予約アクションキャンセル成功後に一覧を再取得させるためのトリガー（値自体に意味はない）
  const [reservedActionsRefreshKey, setReservedActionsRefreshKey] = useState(0);

  // 予約アクション（予約リポスト / 予約削除）のキャンセル確認モーダル。
  // 失敗時にダイアログを閉じずエラーを表示できるよう、専用の状態として管理する。
  const [cancelAction, setCancelAction] = useState<ReservedAction | null>(null);
  const [cancellingAction, setCancellingAction] = useState(false);
  const [cancelActionError, setCancelActionError] = useState('');

  useEffect(() => {
    if (userId) {
      void loadUser();
      void loadPlanAssignments();
      void loadSchedulerAuthorization();
      void loadUserUsage();
    }
  }, [userId]);

  const loadUser = async () => {
    if (!userId) {
      return;
    }

    try {
      const data = await apiClient.getUser(userId);
      setUser(data);
      setUpgradeProhibited(data.upgrade_prohibited);
    } catch (_err) {
      setError('Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  // 予約アクション一覧のロード。ページ/フィルタ変更のたびに再フェッチされるため、
  // 古いリクエストの結果で新しい状態を上書きしないよう cancelled フラグでガードする。
  useEffect(() => {
    if (!userId) {
      return;
    }

    let cancelled = false;

    (async () => {
      setLoadingReservedActions(true);
      try {
        const data = await apiClient.getUserReservedActions(userId, {
          page: reservedActionsPage,
          limit: RESERVED_ACTIONS_PAGE_SIZE,
          status: reservedActionStatusFilter || undefined,
          kind: reservedActionKindFilter || undefined
        });
        if (!cancelled) {
          setReservedActions(data.items);
          setReservedActionsTotal(data.pagination.total);
          setReservedActionsTotalPages(data.pagination.totalPages);
        }
      } catch (_err) {
        console.error('Failed to load reserved actions', _err);
      } finally {
        if (!cancelled) {
          setLoadingReservedActions(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    userId,
    reservedActionsPage,
    reservedActionStatusFilter,
    reservedActionKindFilter,
    reservedActionsRefreshKey
  ]);

  const handleCancelActionRequest = (action: ReservedAction) => {
    setCancelActionError('');
    setCancelAction(action);
  };

  const handleCancelActionDismiss = () => {
    if (cancellingAction) {
      return;
    }
    setCancelAction(null);
    setCancelActionError('');
  };

  const handleCancelActionConfirm = async () => {
    if (!cancelAction) {
      return;
    }

    setCancellingAction(true);
    setCancelActionError('');

    try {
      await apiClient.cancelScheduledAction(cancelAction.id);
      setCancelAction(null);
      setReservedActionsRefreshKey(key => key + 1);
    } catch (err) {
      setCancelActionError(err instanceof Error ? err.message : 'Failed to cancel action');
    } finally {
      setCancellingAction(false);
    }
  };

  const loadPlanAssignments = async () => {
    if (!userId) {
      return;
    }

    setLoadingPlans(true);
    try {
      const data = await apiClient.getUserPlanAssignments(userId);
      setPlanAssignments(data.assignments);
    } catch (_err) {
      console.error('Failed to load plan assignments', _err);
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadUserUsage = async () => {
    if (!userId) {
      return;
    }

    setLoadingUsage(true);
    setUserUsage(null);
    try {
      const data = await apiClient.getUserUsage(userId);
      setUserUsage(data);
    } catch (_err) {
      console.error('Failed to load user usage', _err);
    } finally {
      setLoadingUsage(false);
    }
  };

  const loadSchedulerAuthorization = async () => {
    if (!userId) {
      return;
    }

    setLoadingScheduler(true);
    try {
      const data = await apiClient.getUserSchedulerAuthorization(userId);
      setSchedulerAuthorization(data);
    } catch (_err) {
      console.error('Failed to load scheduler authorization', _err);
      setError('Failed to load scheduler authorization');
    } finally {
      setLoadingScheduler(false);
    }
  };

  const handleRevokeSchedulerAuthorization = async () => {
    if (!userId) {
      return;
    }

    // Close the modal immediately for better UX
    setConfirmModal({
      isOpen: false,
      title: '',
      message: '',
      onConfirm: () => {}
    });

    setSaving(true);
    try {
      await apiClient.revokeUserSchedulerAuthorization(userId);
      // Reload scheduler authorization status
      await loadSchedulerAuthorization();
    } catch (_err) {
      console.error('Failed to revoke scheduler authorization', _err);
      setError('Failed to revoke scheduler authorization');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (newStatus: string) => {
    if (!userId || !user) {
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Change User Status',
      message: `Are you sure you want to change user status to ${newStatus}?`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        setSaving(true);
        setError('');

        try {
          const updated = await apiClient.updateUser(userId, { status: newStatus });
          setUser(updated);
        } catch (_err) {
          setError('Failed to update user status');
        } finally {
          setSaving(false);
        }
      }
    });
  };

  const handleToggleActive = async () => {
    if (!userId || !user) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      const updated = await apiClient.updateUser(userId, {
        is_active: !user.is_active
      });
      setUser(updated);
    } catch (_err) {
      setError('Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokePlanAssignment = (assignment: PlanAssignment) => {
    if (!userId) {
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Revoke Plan Assignment',
      message: `Are you sure you want to revoke the ${assignment.plan.displayName.en || assignment.plan.name} plan assignment? This action cannot be undone.`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        setSaving(true);
        setError('');

        try {
          await apiClient.revokeUserPlanAssignment(userId, assignment.id);
          await loadPlanAssignments();
        } catch (_err) {
          setError('Failed to revoke plan assignment');
        } finally {
          setSaving(false);
        }
      }
    });
  };

  const handleToggleUpgradeProhibited = async () => {
    if (!userId) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      const result = await apiClient.updateUserRestrictions(userId, {
        upgradeProhibited: !upgradeProhibited
      });
      setUpgradeProhibited(result.upgradeProhibited);
    } catch (_err) {
      setError('Failed to update upgrade restrictions');
    } finally {
      setSaving(false);
    }
  };

  const handleSetSuspension = () => {
    if (!userId) {
      return;
    }

    const suspensionTypeText = suspensionType === 'temporary' ? 'temporary' : 'permanent';
    const durationText =
      suspensionType === 'temporary'
        ? suspensionUntil
          ? `until ${suspensionUntil}`
          : `for ${suspensionDays} days`
        : 'permanently';

    setConfirmModal({
      isOpen: true,
      title: 'Set User Suspension',
      message: `Are you sure you want to set ${suspensionTypeText} ${suspensionStatus.toLowerCase()} ${durationText}?\n\n⚠️ All pending scheduled posts and scheduled actions (reposts / deletions) for this user will be CANCELLED. They are NOT restored when the suspension is lifted.${suspensionReason ? `\n\nReason: ${suspensionReason}` : ''}`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        setSaving(true);
        setError('');

        try {
          const payload: {
            status: 'SUSPENDED' | 'BLOCKED';
            suspensionType: 'temporary' | 'permanent';
            days?: number;
            until?: string;
            reason?: string;
          } = {
            status: suspensionStatus,
            suspensionType
          };

          if (suspensionType === 'temporary') {
            if (suspensionUntil) {
              payload.until = new Date(suspensionUntil).toISOString();
            } else {
              payload.days = parseInt(suspensionDays, 10);
            }
          }

          if (suspensionReason) {
            payload.reason = suspensionReason;
          }

          const result = await apiClient.setSuspension(userId, payload);
          setUser(prev =>
            prev
              ? {
                  ...prev,
                  status: result.user.status,
                  suspended_until: result.user.suspendedUntil,
                  suspension_reason: result.user.suspensionReason
                }
              : null
          );

          // Reset form
          setSuspensionType('temporary');
          setSuspensionDays('7');
          setSuspensionUntil('');
          setSuspensionReason('');
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to set suspension');
        } finally {
          setSaving(false);
        }
      }
    });
  };

  const handleLiftSuspension = () => {
    if (!userId) {
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Lift User Suspension',
      message:
        'Are you sure you want to lift the suspension and restore this user to ACTIVE status?',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        setSaving(true);
        setError('');

        try {
          await apiClient.liftSuspension(userId);
          setUser(prev =>
            prev
              ? {
                  ...prev,
                  status: UserStatus.ACTIVE,
                  suspended_until: null,
                  suspension_reason: null
                }
              : null
          );
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to lift suspension');
        } finally {
          setSaving(false);
        }
      }
    });
  };

  if (loading) {
    return <div className='text-gray-700 dark:text-gray-300'>Loading...</div>;
  }

  if (!user) {
    return <div className='text-gray-700 dark:text-gray-300'>User not found</div>;
  }

  return (
    <div>
      <button
        onClick={() => navigate('/users')}
        className='mb-4 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
      >
        ← Back to Users
      </button>

      <h1 className='mb-6 text-3xl font-bold text-gray-800 dark:text-white'>User Details</h1>

      {error && (
        <div className='mb-4 rounded-md bg-red-50 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-200'>
          {error}
        </div>
      )}

      {/* User Status Reference */}
      <UserStatusReference />

      {/* User Information */}
      <div className='mb-6 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'>
        <div className='mb-4 flex items-start justify-between'>
          <div>
            <h2 className='text-2xl font-semibold text-gray-800 dark:text-white'>
              {user.display_name ?? user.handle}
            </h2>
            <div className='flex items-center gap-2'>
              <p className='text-gray-600 dark:text-gray-300'>@{user.handle}</p>
              <a
                href={`https://bsky.app/profile/${user.handle}`}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-1 rounded-md bg-blue-500 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-blue-600'
                title='View on Bluesky'
              >
                <BlueskyIcon className='h-3 w-3' />
                Bluesky
              </a>
            </div>
            <p className='text-sm text-gray-500 dark:text-gray-400'>{user.did}</p>
          </div>
          {user.avatar && (
            <img src={user.avatar} alt={user.handle} className='h-16 w-16 rounded-full' />
          )}
        </div>

        <div className='grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2 dark:border-gray-700'>
          <div>
            <p className='text-sm text-gray-600 dark:text-gray-400'>Created</p>
            <p className='font-medium dark:text-gray-200'>
              {formatDateTime(user.created_at, { dateStyle: 'medium', timeStyle: 'short' }) ||
                'N/A'}
            </p>
          </div>
          <div>
            <p className='text-sm text-gray-600 dark:text-gray-400'>Updated</p>
            <p className='font-medium dark:text-gray-200'>
              {formatDateTime(user.updated_at, { dateStyle: 'medium', timeStyle: 'short' }) ||
                'N/A'}
            </p>
          </div>
          <div>
            <p className='text-sm text-gray-600 dark:text-gray-400'>Access Keyword</p>
            <p className='font-medium dark:text-gray-200'>
              {user.has_access_keyword_access ? 'Yes' : 'No'}
            </p>
          </div>
          <div>
            <p className='text-sm text-gray-600 dark:text-gray-400'>Active</p>
            <p className='font-medium dark:text-gray-200'>{user.is_active ? 'Yes' : 'No'}</p>
          </div>
        </div>
      </div>

      {/* User Actions */}
      <div className='mb-6 rounded-lg border-2 border-red-300 bg-red-50 shadow-md dark:border-red-700 dark:bg-red-900/10'>
        <button
          onClick={() => setIsUserManagementExpanded(!isUserManagementExpanded)}
          className='flex w-full items-center justify-between p-6 text-left transition-colors hover:bg-red-100 dark:hover:bg-red-900/20'
        >
          <div className='flex items-center gap-3'>
            <span className='text-2xl'>⚠️</span>
            <div>
              <h3 className='text-xl font-semibold text-red-800 dark:text-red-200'>
                User Management
              </h3>
              <p className='text-sm text-red-600 dark:text-red-400'>
                Critical operations - use with caution
              </p>
            </div>
          </div>
          <svg
            className={`h-6 w-6 text-red-800 transition-transform dark:text-red-200 ${
              isUserManagementExpanded ? 'rotate-180' : ''
            }`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
          </svg>
        </button>

        {isUserManagementExpanded && (
          <div className='space-y-4 border-t-2 border-red-300 bg-red-50 p-6 text-red-800 dark:border-red-700 dark:bg-red-900/10 dark:text-red-200'>
            <div>
              <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                Status
              </label>
              <div className='flex gap-2'>
                {[
                  UserStatus.ACTIVE,
                  UserStatus.RESTRICTED,
                  UserStatus.SUSPENDED,
                  UserStatus.BLOCKED
                ].map(status => (
                  <button
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    disabled={saving || user.status === status}
                    className={`rounded-md px-4 py-2 font-medium transition-colors ${
                      user.status === status
                        ? status === UserStatus.ACTIVE
                          ? 'bg-green-600 text-white dark:bg-green-700'
                          : 'bg-red-600 text-white dark:bg-red-700'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                    } disabled:bg-gray-100 disabled:text-gray-400 dark:disabled:bg-gray-800 dark:disabled:text-gray-600`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <button
                onClick={handleToggleActive}
                disabled={saving}
                className={`rounded-md px-4 py-2 font-medium text-white transition-colors ${
                  user.is_active
                    ? 'bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-700 dark:hover:bg-yellow-600'
                    : 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600'
                } disabled:bg-gray-400 dark:disabled:bg-gray-700`}
              >
                {user.is_active ? 'Deactivate User' : 'Activate User'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Account Suspension */}
      <div className='mb-6 rounded-lg border-2 border-red-300 bg-red-50 shadow-md dark:border-red-700 dark:bg-red-900/10'>
        <div className='flex items-center justify-between p-6'>
          <button
            onClick={() => setIsSuspensionExpanded(!isSuspensionExpanded)}
            className='flex flex-1 items-center justify-between text-left transition-colors hover:opacity-80'
          >
            <div className='flex items-center gap-3'>
              <span className='text-2xl'>🚫</span>
              <div>
                <h3 className='text-xl font-semibold text-red-800 dark:text-red-200'>
                  Account Suspension
                </h3>
                <p className='text-sm text-red-600 dark:text-red-400'>
                  Temporarily or permanently suspend user accounts
                </p>
              </div>
            </div>
            <svg
              className={`h-6 w-6 text-red-800 transition-transform dark:text-red-200 ${
                isSuspensionExpanded ? 'rotate-180' : ''
              }`}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 9l-7 7-7-7'
              />
            </svg>
          </button>
          <a
            href={`/users/${userId}/status-logs`}
            target='_blank'
            rel='noopener noreferrer'
            className='ml-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 dark:bg-indigo-700 dark:hover:bg-indigo-600'
            onClick={e => e.stopPropagation()}
          >
            View Status History
          </a>
        </div>

        {isSuspensionExpanded && (
          <div className='space-y-4 border-t-2 border-red-300 bg-red-50 p-6 text-red-800 dark:border-red-700 dark:bg-red-900/10 dark:text-red-200'>
            {/* Current Suspension Status */}
            {(user.status === UserStatus.SUSPENDED || user.status === UserStatus.BLOCKED) && (
              <div className='mb-4 rounded-lg border-2 border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20'>
                <div className='flex items-start justify-between'>
                  <div>
                    <h4 className='mb-2 text-lg font-semibold text-red-800 dark:text-red-200'>
                      ⚠️ User is currently {user.status.toLowerCase()}
                    </h4>
                    {user.suspended_until && (
                      <>
                        <p className='text-sm text-red-700 dark:text-red-300'>
                          <span className='font-medium'>Suspended Until (Local):</span>{' '}
                          {formatDateTime(user.suspended_until, {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          }) || 'N/A'}
                        </p>
                        <p className='text-xs text-red-600 dark:text-red-400'>
                          <span className='font-medium'>UTC:</span>{' '}
                          {new Date(user.suspended_until).toISOString()}
                        </p>
                        <p className='text-xs text-red-600 dark:text-red-400'>
                          <span className='font-medium'>Time Remaining:</span>{' '}
                          {calculateTimeRemaining(user.suspended_until)}
                        </p>
                      </>
                    )}
                    {!user.suspended_until && (
                      <p className='text-sm text-red-700 dark:text-red-300'>
                        <span className='font-medium'>Type:</span> Permanent Suspension
                      </p>
                    )}
                    {user.suspension_reason && (
                      <p className='mt-2 text-sm text-red-700 dark:text-red-300'>
                        <span className='font-medium'>Reason:</span> {user.suspension_reason}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleLiftSuspension}
                    disabled={saving}
                    className='rounded-md bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700 disabled:bg-gray-400 dark:bg-green-700 dark:hover:bg-green-600 dark:disabled:bg-gray-700'
                  >
                    Lift Suspension
                  </button>
                </div>
              </div>
            )}

            {/* Suspension Form */}
            <div className='space-y-4'>
              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Suspension Type
                </label>
                <div className='flex gap-4'>
                  <label className='flex items-center'>
                    <input
                      type='radio'
                      value='temporary'
                      checked={suspensionType === 'temporary'}
                      onChange={e => setSuspensionType(e.target.value as 'temporary' | 'permanent')}
                      className='mr-2'
                    />
                    <span className='text-gray-700 dark:text-gray-300'>Temporary</span>
                  </label>
                  <label className='flex items-center'>
                    <input
                      type='radio'
                      value='permanent'
                      checked={suspensionType === 'permanent'}
                      onChange={e => setSuspensionType(e.target.value as 'temporary' | 'permanent')}
                      className='mr-2'
                    />
                    <span className='text-gray-700 dark:text-gray-300'>Permanent</span>
                  </label>
                </div>
              </div>

              {suspensionType === 'temporary' && (
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                  <div>
                    <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                      Duration (days)
                    </label>
                    <input
                      type='number'
                      min='1'
                      max='365'
                      value={suspensionDays}
                      onChange={e => {
                        setSuspensionDays(e.target.value);
                        setSuspensionUntil(''); // Clear specific date when days is set
                      }}
                      disabled={!!suspensionUntil}
                      className='w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800'
                    />
                  </div>
                  <div>
                    <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                      Or Until Date
                    </label>
                    <input
                      type='datetime-local'
                      value={suspensionUntil}
                      onChange={e => {
                        setSuspensionUntil(e.target.value);
                        setSuspensionDays(''); // Clear days when specific date is set
                      }}
                      disabled={!!suspensionDays && suspensionDays !== '0'}
                      className='w-full rounded-md border border-gray-300 px-3 py-2 disabled:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:disabled:bg-gray-800'
                    />
                  </div>
                </div>
              )}

              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Status Type
                </label>
                <select
                  value={suspensionStatus}
                  onChange={e => setSuspensionStatus(e.target.value as 'SUSPENDED' | 'BLOCKED')}
                  className='w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                >
                  <option value='SUSPENDED'>Suspended</option>
                  <option value='BLOCKED'>Blocked</option>
                </select>
              </div>

              <div>
                <label className='mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300'>
                  Reason (Optional)
                </label>
                <textarea
                  value={suspensionReason}
                  onChange={e => setSuspensionReason(e.target.value)}
                  rows={3}
                  placeholder='Enter reason for suspension...'
                  className='w-full rounded-md border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
                />
              </div>

              <button
                onClick={handleSetSuspension}
                disabled={
                  saving || (suspensionType === 'temporary' && !suspensionDays && !suspensionUntil)
                }
                className='rounded-md bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:bg-gray-400 dark:bg-red-700 dark:hover:bg-red-600 dark:disabled:bg-gray-700'
              >
                Set Suspension
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Current Plan & Usage */}
      <div className='mb-6'>
        <PlanUsageDisplay planInfo={userUsage} loading={loadingUsage} />
      </div>

      {/* Plan Information */}
      <div className='mb-6 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'>
        <div className='mb-4 flex items-center justify-between'>
          <h3 className='text-xl font-semibold text-gray-700 dark:text-gray-200'>
            Plan Assignments
          </h3>
          <button
            onClick={handleToggleUpgradeProhibited}
            disabled={saving}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white transition-colors ${
              upgradeProhibited
                ? 'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600'
                : 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600'
            } disabled:bg-gray-400 dark:disabled:bg-gray-700`}
          >
            {upgradeProhibited ? 'Enable Upgrades' : 'Prohibit Upgrades'}
          </button>
        </div>

        {upgradeProhibited && (
          <div className='mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200'>
            ⚠️ Plan upgrades are currently prohibited for this user
          </div>
        )}

        {loadingPlans ? (
          <p className='text-gray-500 dark:text-gray-400'>Loading plan assignments...</p>
        ) : planAssignments.length === 0 ? (
          <p className='text-gray-500 dark:text-gray-400'>No plan assignments</p>
        ) : (
          <div className='space-y-4'>
            {planAssignments.map(assignment => (
              <div
                key={assignment.id}
                className='rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-700'
              >
                <div className='mb-3 flex items-start justify-between'>
                  <div>
                    <h4 className='text-lg font-semibold text-gray-800 dark:text-white'>
                      {assignment.plan.displayName.en || assignment.plan.name}
                    </h4>
                    <p className='text-sm text-gray-600 dark:text-gray-300'>
                      Tier: <span className='font-medium'>{assignment.plan.tier}</span>
                    </p>
                    {assignment.ticket && (
                      <p className='text-xs text-gray-500 dark:text-gray-400'>
                        Ticket Code: <span className='font-mono'>{assignment.ticket.code}</span>
                      </p>
                    )}
                  </div>
                  <div className='flex flex-col items-end gap-2'>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        assignment.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : assignment.status === 'EXPIRED'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {assignment.status}
                    </span>
                    {assignment.status === 'ACTIVE' && (
                      <button
                        onClick={() => handleRevokePlanAssignment(assignment)}
                        disabled={saving}
                        className='rounded-md bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:bg-gray-400 dark:bg-red-700 dark:hover:bg-red-600 dark:disabled:bg-gray-800'
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </div>

                <div className='grid grid-cols-1 gap-3 border-t pt-3 text-sm md:grid-cols-2 dark:border-gray-600'>
                  <div>
                    <p className='text-gray-600 dark:text-gray-400'>Activated At</p>
                    <p className='font-medium dark:text-gray-200'>
                      {formatDateTime(assignment.activatedAt, {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      }) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className='text-gray-600 dark:text-gray-400'>Expires At</p>
                    <p className='font-medium dark:text-gray-200'>
                      {assignment.expiresAt
                        ? formatDateTime(assignment.expiresAt, {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          }) || 'N/A'
                        : 'Never'}
                    </p>
                  </div>
                </div>

                <div className='mt-3 border-t pt-3 dark:border-gray-600'>
                  <p className='mb-2 text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Plan Limits
                  </p>
                  <div className='grid grid-cols-1 gap-2 text-xs text-gray-600 md:grid-cols-2 dark:text-gray-400'>
                    {Object.entries(assignment.plan.limits).map(([key, value]) => (
                      <div key={key}>
                        <span className='font-medium'>{key}:</span>{' '}
                        <span>{value === null ? 'N/A' : String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scheduler Authorization */}
      <div className='mb-6 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'>
        <button
          onClick={() => setIsSchedulerExpanded(!isSchedulerExpanded)}
          className='mb-4 flex w-full items-center justify-between text-left'
        >
          <h3 className='text-xl font-semibold text-gray-700 dark:text-gray-200'>
            Scheduler Authorization
          </h3>
          <svg
            className={`h-5 w-5 transform transition-transform ${isSchedulerExpanded ? 'rotate-180' : ''}`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
          </svg>
        </button>

        {isSchedulerExpanded && (
          <div>
            {loadingScheduler ? (
              <p className='text-gray-500 dark:text-gray-400'>Loading scheduler authorization...</p>
            ) : !schedulerAuthorization?.authorized ? (
              <div className='rounded-md bg-gray-50 p-4 dark:bg-gray-700'>
                <p className='text-sm text-gray-600 dark:text-gray-300'>
                  This user has not authorized scheduler access.
                </p>
              </div>
            ) : (
              <div className='space-y-4'>
                {/* Authorization Status */}
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-700'>
                  <div className='mb-3 flex items-start justify-between'>
                    <div>
                      <h4 className='text-lg font-semibold text-gray-800 dark:text-white'>
                        Status
                      </h4>
                      <div className='mt-2'>
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                            schedulerAuthorization.token?.is_active
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          }`}
                        >
                          {schedulerAuthorization.token?.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    {schedulerAuthorization.token?.is_active && (
                      <button
                        onClick={() => {
                          setConfirmModal({
                            isOpen: true,
                            title: 'Revoke Scheduler Authorization',
                            message:
                              'Are you sure you want to revoke scheduler authorization for this user? Scheduled posts will no longer be published until they re-authorize.',
                            onConfirm: handleRevokeSchedulerAuthorization
                          });
                        }}
                        disabled={saving}
                        className='rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:bg-gray-400 dark:bg-red-700 dark:hover:bg-red-600 dark:disabled:bg-gray-700'
                      >
                        {saving ? 'Revoking...' : 'Revoke'}
                      </button>
                    )}
                  </div>

                  {/* Token Details */}
                  {schedulerAuthorization.token && (
                    <div className='mt-4 grid grid-cols-2 gap-4 border-t pt-4 dark:border-gray-600'>
                      <div>
                        <p className='text-sm text-gray-600 dark:text-gray-400'>Created At</p>
                        <p className='mt-1 text-sm font-medium dark:text-gray-200'>
                          {formatDateTime(schedulerAuthorization.token.created_at, {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          }) || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm text-gray-600 dark:text-gray-400'>Last Used At</p>
                        <p className='mt-1 text-sm font-medium dark:text-gray-200'>
                          {formatDateTime(schedulerAuthorization.token.last_used_at, {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          }) || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm text-gray-600 dark:text-gray-400'>Expires At</p>
                        <p className='mt-1 text-sm font-medium dark:text-gray-200'>
                          {formatDateTime(schedulerAuthorization.token.refresh_expires_at, {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          }) || 'N/A'}
                        </p>
                        <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                          {new Date(schedulerAuthorization.token.refresh_expires_at) < new Date()
                            ? 'Expired'
                            : calculateTimeRemaining(
                                schedulerAuthorization.token.refresh_expires_at,
                                { showDays: true }
                              )}
                        </p>
                      </div>
                      <div>
                        <p className='text-sm text-gray-600 dark:text-gray-400'>Updated At</p>
                        <p className='mt-1 text-sm font-medium dark:text-gray-200'>
                          {formatDateTime(schedulerAuthorization.token.updated_at, {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          }) || 'N/A'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reserved Actions（予約投稿・予約リポスト・予約削除の統合一覧） */}
      <div className='rounded-lg bg-white p-6 shadow-md dark:bg-gray-800'>
        <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
          <h3 className='text-xl font-semibold text-gray-700 dark:text-gray-200'>
            Reserved Actions
          </h3>
          <Link
            to={`/reserved-actions?userId=${userId ?? ''}`}
            className='text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
          >
            View All Posts →
          </Link>
        </div>

        {/* Filters */}
        <div className='mb-4 flex flex-wrap gap-3'>
          <select
            value={reservedActionKindFilter}
            onChange={e => {
              setReservedActionKindFilter(e.target.value);
              setReservedActionsPage(1);
            }}
            className='rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white'
          >
            <option value=''>All Kinds</option>
            <option value='POST'>Post</option>
            <option value='REPOST'>Repost</option>
            <option value='DELETE_POST'>Delete</option>
          </select>

          <select
            value={reservedActionStatusFilter}
            onChange={e => {
              setReservedActionStatusFilter(e.target.value);
              setReservedActionsPage(1);
            }}
            className='rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white'
          >
            <option value=''>All Statuses</option>
            <option value='PENDING'>Pending</option>
            <option value='EXECUTING'>Executing</option>
            <option value='COMPLETED'>Completed</option>
            <option value='FAILED'>Failed</option>
            <option value='CANCELLED'>Cancelled</option>
          </select>
        </div>

        {loadingReservedActions ? (
          <p className='text-gray-500 dark:text-gray-400'>Loading reserved actions...</p>
        ) : reservedActions.length === 0 ? (
          <p className='text-gray-500 dark:text-gray-400'>No reserved actions</p>
        ) : (
          <div className='space-y-0'>
            {reservedActions.map(action => (
              <ReservedActionListItem
                key={action.id}
                action={action}
                onCancel={handleCancelActionRequest}
                cancellingActionId={cancellingAction ? (cancelAction?.id ?? null) : null}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {reservedActionsTotalPages > 1 && (
          <div className='mt-4 flex flex-col items-center gap-3 border-t pt-4 md:flex-row md:justify-between dark:border-gray-700'>
            <div className='text-sm text-gray-700 dark:text-gray-300'>
              Page {reservedActionsPage} of {reservedActionsTotalPages} ({reservedActionsTotal}{' '}
              total)
            </div>
            <div className='flex gap-2'>
              <button
                onClick={() => setReservedActionsPage(p => Math.max(1, p - 1))}
                disabled={reservedActionsPage <= 1}
                className='rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-600'
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setReservedActionsPage(p => Math.min(reservedActionsTotalPages, p + 1))
                }
                disabled={reservedActionsPage >= reservedActionsTotalPages}
                className='rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-600'
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText='Confirm'
        cancelText='Cancel'
        onConfirm={confirmModal.onConfirm}
        onCancel={() =>
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
        }
        variant='danger'
      />

      <ConfirmModal
        isOpen={!!cancelAction}
        title='Stop Reserved Action'
        message={`Are you sure you want to stop this reserved action from executing? This only stops the scheduled execution — the record itself will not be deleted.${
          cancelActionError ? ` (Error: ${cancelActionError})` : ''
        }`}
        confirmText='Stop Execution'
        cancelText='Close'
        onConfirm={() => void handleCancelActionConfirm()}
        onCancel={handleCancelActionDismiss}
        variant='danger'
        isLoading={cancellingAction}
      />
    </div>
  );
}
